import axios from 'axios'

const api = axios.create({ baseURL: '/api' })

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('fm_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('fm_token')
      window.location.href = '/login'
    }
    return Promise.reject(err)
  }
)

export default api

// ── Auth ──────────────────────────────────────────────────────────────────
export const login = (email: string, password: string) =>
  api.post<{ access_token: string }>('/auth/login', { email, password })

export const register = (email: string, password: string, full_name: string) =>
  api.post('/auth/register', { email, password, full_name })

export const getMe = () => api.get('/auth/me')

// ── Ingest (/ingest/*) ───────────────────────────────────────────────────
export const ingestWearable = (data: { atom_type: string; payload: Record<string, unknown>; source?: string; strength?: number; recorded_at?: string }) =>
  api.post('/ingest/wearable', data)

export const ingestLab = (data: { name: string; value: number; unit: string; ref_range?: Record<string, number>; lab?: string; confidence?: number; recorded_at?: string }) =>
  api.post('/ingest/lab', data)

export const ingestSymptom = (data: { symptom: string; severity: number; onset?: string; duration_days?: number; body_location?: string }) =>
  api.post('/ingest/symptom', data)

export const ingestMedication = (data: { name: string; dose: string; frequency: string; start?: string; end?: string }) =>
  api.post('/ingest/medication', data)

export const ingestAdherence = (data: { medication_id: string; taken: boolean; taken_at?: string }) =>
  api.post('/ingest/adherence', data)

export const ingestContext = (data: { note: string; category?: string; date?: string }) =>
  api.post('/ingest/context', data)

export const ingestFhir = (fhir_bundle: Record<string, unknown>) =>
  api.post('/ingest/fhir', { fhir_bundle })

export const ingestVcf = (vcf_text: string) =>
  api.post('/ingest/vcf', { vcf_text })

export const ingestAtom = (data: { atom_type: string; payload: Record<string, unknown>; strength?: number; data_source?: string; recorded_at?: string }) =>
  api.post('/ingest/atom', data)

export const getAtoms = (atom_type?: string) =>
  api.get('/ingest/atoms', { params: { atom_type, limit: 200 } })

export const getAtomTypes = () => api.get('/ingest/types')
export const getIngestStatus = () => api.get('/ingest/status')
export const getPendingReview = () => api.get('/ingest/pending-review')
export const confirmLab = (atom_id: string, confirmed_value: number) =>
  api.post('/ingest/confirm-lab', null, { params: { atom_id, confirmed_value } })

// ── Timeline (/timeline/*) ───────────────────────────────────────────────
export const getTimeline = (person_id: string, params?: { atom_type?: string; limit?: number; start_date?: string; end_date?: string }) =>
  api.get(`/timeline/${person_id}`, { params })

export const getAtomDetail = (person_id: string, atom_id: string) =>
  api.get(`/timeline/${person_id}/atom/${atom_id}`)

export const getTimelineChains = (person_id: string) =>
  api.get(`/timeline/${person_id}/chains`)

export const getTimelineTrends = (person_id: string) =>
  api.get(`/timeline/${person_id}/trends`)

export const annotateTimeline = (person_id: string, data: { note: string; category?: string; date?: string }) =>
  api.post(`/timeline/${person_id}/annotate`, data)

export const exportTimelinePdf = (person_id: string) =>
  api.get(`/timeline/${person_id}/export/pdf`)

export const getTimelineSummary = () => api.get('/timeline/summary/counts')

// ── Reasoning (/reason/*) ────────────────────────────────────────────────
export const runPLN = (time_window_days = 365) =>
  api.post('/reason/personal', null, { params: { time_window_days } })

export const runFamilyReasoning = () => api.post('/reason/family')

export const getChains = (person_id: string) =>
  api.get(`/reason/chains/${person_id}`)

export const getRisks = (person_id: string) =>
  api.get(`/reason/risks/${person_id}`)

export const getRecommendations = (person_id: string) =>
  api.get(`/reason/recommendations/${person_id}`)

export const explainChain = (chain_id: string) =>
  api.post('/reason/explain', { chain_id })

export const submitChainFeedback = (chain_id: string, feedback_text: string) =>
  api.post('/reason/feedback', { chain_id, feedback_text })

export const getUngroundedChains = (person_id: string) =>
  api.get(`/reason/ungrounded/${person_id}`)

export const runMonitor = () => api.post('/reason/monitor/run')

// ── Monitor (/monitor/*) ─────────────────────────────────────────────────
export const getAlerts = (person_id: string, status = 'active') =>
  api.get(`/monitor/alerts/${person_id}`, { params: { status } })

export const getAlertHistory = (person_id: string) =>
  api.get(`/monitor/alerts/history/${person_id}`)

export const snoozeAlert = (alert_id: string, duration_days: number) =>
  api.post('/monitor/snooze', { alert_id, duration_days })

export const alertFeedback = (alert_id: string, was_useful: boolean) =>
  api.post('/monitor/feedback', { alert_id, was_useful })

export const getDoctorBrief = (_person_id: string) =>
  api.post('/reason/brief')

export const getDoctorBriefFhir = (person_id: string) =>
  api.get(`/monitor/brief/${person_id}/fhir`)

export const shareBrief = (expiry_days = 7) =>
  api.post('/monitor/brief/share', null, { params: { expiry_days } })

export const createAppointment = (data: { scheduled_at: string; doctor_name?: string; notes?: string }) =>
  api.post('/monitor/appointment', data)

export const postAppointmentLog = (data: { appointment_id: string; findings: string; diagnosis_icd11?: string; medications_prescribed?: string[]; tests_ordered?: string[]; follow_up?: string }) =>
  api.post('/monitor/appointment/post-log', data)

export const getWeeklyDigest = (person_id: string) =>
  api.get(`/monitor/digest/${person_id}`)

export const updateAlertStatus = (id: string, new_status: string) =>
  api.patch(`/reason/alerts/${id}`, null, { params: { new_status } })

// ── Family (/family/*) ───────────────────────────────────────────────────
export const getFamilyGraph = () => api.get('/family/graph')

export const addFamilyMember = (data: { relation: string; display_name: string; birth_year?: number; is_proxy?: boolean; proxy_data?: Record<string, unknown> }) =>
  api.post('/family/members', data)

export const addProxyMember = (data: { relation: string; display_name: string; birth_year?: number; sex?: string; death_year?: number; cause_of_death?: string; known_conditions?: Array<{ name: string; onset_age?: number; icd11_code?: string }> }) =>
  api.post('/family/proxy/add', data)

export const updateProxyMember = (proxy_id: string, data: Record<string, unknown>) =>
  api.put(`/family/proxy/${proxy_id}`, data)

export const inviteFamilyMember = (invitee_did: string, relation: string) =>
  api.post('/family/invite', null, { params: { invitee_did, relation } })

export const runMOSES = () => api.post('/family/moses/run')

export const getHereditaryRisks = () => api.get('/family/risks')

export const getCaregiverDashboard = (caregiver_did: string) =>
  api.get(`/family/dashboard/${caregiver_did}`)

// ── Consent (/consent/* /identity/*) ────────────────────────────────────
export const grantConsent = (data: { grantee_did: string; scope: string[]; permission: string; purpose?: string; family_relation?: string; valid_until?: string; anonymization?: Record<string, unknown> }) =>
  api.post('/consent/grant', data)

export const revokeConsent = (contract_id: string) =>
  api.post('/consent/revoke', { contract_id })

export const getConsents = (person_id: string) =>
  api.get(`/consent/list/${person_id}`)

export const getConsentAudit = (person_id: string) =>
  api.get(`/consent/audit/${person_id}`)

export const exportConsents = (person_id: string) =>
  api.get(`/consent/export/${person_id}`)

// ── MKG (/mkg/*) ─────────────────────────────────────────────────────────
export const getMkgReference = (biomarker: string, sex: string, age: string) =>
  api.get(`/mkg/reference/${biomarker}/${sex}/${age}`)

export const getMkgInteractions = (substance: string) =>
  api.get(`/mkg/interactions/${substance}`)

export const getMkgPenetrance = (gene: string, variant: string) =>
  api.get(`/mkg/penetrance/${gene}/${variant}`)

export const getMkgGuidelines = (condition: string) =>
  api.get(`/mkg/guidelines/${condition}`)

export const getMkgVersion = () => api.get('/mkg/version')
export const getMkgUpdates = () => api.get('/mkg/updates')
export const getMkgStatus = () => api.get('/mkg/status')

// ── Token (/token/*) ─────────────────────────────────────────────────────
export const getTokenBalance = (person_id: string) => api.get(`/token/balance/${person_id}`)
export const getTokenDashboard = (person_id: string) => api.get(`/token/dashboard/${person_id}`)
export const getTokenPricing = () => api.get('/token/pricing')
export const subscribePlan = (tier: string) => api.post('/token/subscribe', { tier })

// ── Rejuve (/rejuve/*) ───────────────────────────────────────────────────
export const rejuveOptIn = (scope_config: Record<string, unknown>) =>
  api.post('/rejuve/opt-in', { scope_config })
export const rejuveOptOut = () => api.post('/rejuve/opt-out')
export const getRejuveContribution = (person_id: string) =>
  api.get(`/rejuve/contribution/${person_id}`)
export const getRejuveMatches = (person_id: string) =>
  api.get(`/rejuve/matches/${person_id}`)
